'use client';
import { IGallery } from '@/_types';
import React, { useMemo } from 'react';
import MultipleSlider from '../../GallerySlider';
import { Box, Container, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import Breadcrumbs from '@/_components/Breadcrumbs';
import { useTranslations } from 'next-intl';
import { getPublicSeoOrigin } from '@/i18n/urls'

const Desktop = ({ data }: { data: IGallery }) => {
  const currentLocale = useCustomLocale();
  const t = useTranslations();

  const breadCrumbs = useMemo(
    () => [
      { title: t('home'), link: '/' },
      { title: t('gallery'), link: '/gallery' },
      { title: data?.title?.[currentLocale] || '' },
    ],
    [t, data?.title, currentLocale],
  );

  const domain = getPublicSeoOrigin();

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      item: item.link ? `${domain}${item.link}` : undefined,
    })),
  };

  return (
    <Box bg='background'>
      <Container maxW={'1200px'} h={'fit'} mb={'100px'} p={0}>
        <Breadcrumbs data={breadCrumbs} />
        <Text mt={4} fontSize={24} fontWeight={700} as='h1'>
          {data?.title?.[currentLocale]}
        </Text>
        <Text mt={2} mb={4} color={'text.muted'}>
          {dayjs(data?.created_at).format('DD.MM.YYYY')}
        </Text>

        <Stack rounded={16} gap={0} overflow={'hidden'} bg={'white'}>
          <MultipleSlider
            maxHeight='600px'
            aspectRatio={3 / 2}
            data={data?.images}
            thumbHeight={'100px'}
          />
          <Text
            as='pre'
            p={6}
            py={8}
            pb={10}
            fontSize={20}
            lineHeight={'28px'}
            style={{ font: 'inherit', whiteSpace: 'pre-wrap' }}
          >
            {data?.description?.[currentLocale]}
          </Text>
        </Stack>
      </Container>

      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </Box>
  );
};

export default Desktop;
